import type { DetailedHTMLProps, HTMLAttributes, InputHTMLAttributes } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, type Theme } from '@emotion/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { type StyledComponent } from '@emotion/styled';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { media } from '@atlaskit/primitives/responsive/media';
import { token } from '@atlaskit/tokens';

import {
	collapsedSearchInputWidth,
	dialogWidth,
	inputFieldHeight,
	grid,
	dialogPageTakeoverWidth,
	atlassianNavigationHeight,
} from '../style-utils';
import type { SearchCSS } from '../theme';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { InputBoxOverlayRight } from './input-box-overlay-right';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { ClearIconWrapper } from './clear-icon-wrapper';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { ArrowIconWrapper } from './arrow-icon-wrapper';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { HiddenTooltip } from './hidden-tooltip';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { SSRsearchInputField } from './ss-rsearch-input-field';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { SSRsearchInputWrapper } from './ss-rsearch-input-wrapper';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { SSRformContainer } from './ss-rform-container';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { SSRsearchInputContainer } from './ss-rsearch-input-container';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { SSRdesktopSearchIconWrapper } from './ss-rdesktop-search-icon-wrapper';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { SSRmobileSearchIconWrapper } from './ss-rmobile-search-icon-wrapper';

const expandedPlaceholderColor = token('color.text.subtlest');

export interface SearchInputFieldProps {
	isExpanded: boolean;
}

export interface IsExpandedProp {
	isExpanded: boolean;
}

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const MobileSearchIconWrapper: StyledComponent<
	{
		theme?: Theme;
		as?: React.ElementType;
	} & IsExpandedProp,
	DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>,
	{}
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression
> = styled.span<IsExpandedProp>`
	display: none;
	@media screen and (max-width: ${dialogPageTakeoverWidth}px) {
		display: ${(props) => (props.isExpanded ? 'none' : 'block')};
	}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DesktopSearchIconWrapper: StyledComponent<
	{
		theme?: Theme;
		as?: React.ElementType;
	} & SearchCSS &
		IsExpandedProp,
	DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>,
	{}
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression
> = styled('span')<SearchCSS & IsExpandedProp>`
	width: ${grid.multiple(2).px};
	height: ${grid.multiple(2).px};
	position: absolute;
	top: ${grid.px};
	left: ${grid.px};
	color: ${(props) => (props.isExpanded ? token('color.text') : props.default.color)};
	display: inline-flex;
	align-items: baseline;

	:focus {
		box-shadow: 0 0 0 2px ${token('color.border.focused')};
		border-radius: ${token('radius.full')};
		outline: none;
	}

	/* Override the out-of-the-box SearchIcon for vertical centering. */
	& > span[role='img'] {
		line-height: 0;
	}

	@media screen and (max-width: ${dialogPageTakeoverWidth}px) {
		display: none;
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SearchInputContainer: StyledComponent<
	{
		theme?: Theme;
		as?: React.ElementType;
	} & SearchCSS &
		IsExpandedProp & {
			shouldFillContainer?: boolean;
		},
	DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
	{}
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
> = styled('div')<SearchCSS & IsExpandedProp & { shouldFillContainer?: boolean }>(
	// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	css`
		position: relative;
		max-width: 100%;
		${
			/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ' '
		}
		@media screen and (max-width: ${dialogPageTakeoverWidth}px) {
			${/* eslint-enable */ ''}
			width: 100%;
		}
	`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		const boundedStyles = css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
			width: props.isExpanded ? dialogWidth : collapsedSearchInputWidth,
		});

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		const fluidStyles = css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
			width: '100%',
		});

		const styles = props.shouldFillContainer ? fluidStyles : boundedStyles;

		// todo - this should be another class
		if (props.isExpanded) {
			// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			return css`
				${styles}
				${
					/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''
				}
				@media screen and (max-width: ${dialogPageTakeoverWidth}px) {
					width: 100%;
					height: ${atlassianNavigationHeight};
					background-color: ${props.default.backgroundColor};
					${/* eslint-enable */ ''}
					display: flex;
					align-items: center;
					max-width: initial;
				}
			`;
		}
		return styles;
	},
);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SearchInputField: StyledComponent<
	{
		theme?: Theme;
		as?: React.ElementType;
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression
	} & SearchCSS &
		IsExpandedProp,
	DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
	{}
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression
> = styled.input<SearchCSS & IsExpandedProp>`
	height: ${inputFieldHeight.px};
	width: 100%;
	padding: 0px ${token('space.150')} 0px 30px;
	box-sizing: border-box;
	outline: none;
	border-radius: ${token('radius.medium')};
	/* font needs to be a minimum of 16px to prevent iOS Safari from zooming in on focus. Smaller font size is appled in breakpoint below */
	/* Refer to @atlaskit/textfield for more details */
	font: ${token('font.body.large')};
	${(props) => `
    border: ${token('border.width')} solid ${
			props.isExpanded ? props.focus.borderColor : props.default.borderColor
		};
    box-shadow: ${
			props.isExpanded
				? `inset 0 0 0 ${token('border.width')} ${
						props.focus.borderColor
					}, -12px 0 24px 0 ${props.default.backgroundColor}`
				: 'none'
		};
    background-color: ${
			props.isExpanded ? props.focus.backgroundColor : props.default.backgroundColor
		};
    color: ${props.isExpanded ? props.focus.color : props.default.color};

    &::placeholder {
      color: ${props.isExpanded ? expandedPlaceholderColor : props.default.color};
    }
    :focus {
      border: ${token('border.width')} solid ${props.focus.borderColor};
    }
    @media screen and (max-width: ${dialogPageTakeoverWidth}px) {
      ${
				props.isExpanded
					? `
            padding: 0 ${grid.multiple(1.5).px} ${grid.multiple(0.25).px};
            margin: 0 ${grid.multiple(3).px} 0 0;`
					: `
            display: none;
          `
			}
    }
  `}
	${media.above.xs} {
		font: ${token('font.body')};
	}
`;
